import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

import { useMapContext } from '../../../../app/maps/components/Map'
import { FALLBACK_PERF_RESPONSE } from '../../../../app/maps/constants/constants'
import * as mapsApi from '../../../../app/maps/services/api'
import { MapSubtypeId, PerfData } from '../../../../app/maps/types'
import { ScreenerQuery } from '../../../../app/screener/utils'
import { PortfolioGroup } from '../types'
import { getPortfolioRefreshInterval } from '../utils'

export function useRefetchPerf({
  enabled,
  subtype,
  portfolioEntries,
}: {
  enabled?: boolean
  subtype: MapSubtypeId
  portfolioEntries: PortfolioGroup[]
}) {
  const { treemap } = useMapContext()
  const queryClient = useQueryClient()
  const dataVersion = React.useRef<number>()
  const tickers = React.useMemo(
    () =>
      portfolioEntries
        .map((entry) => entry.ticker)
        .filter((ticker, index, arr) => arr.indexOf(ticker) === index)
        .join(','),
    [portfolioEntries]
  )
  const queryKey = ['mapPerf', subtype, tickers]

  const perfDataQuery = useQuery({
    queryKey,
    refetchInterval: getPortfolioRefreshInterval,
    staleTime: 1000,
    enabled: enabled ?? (!!treemap && !subtype.startsWith('portfolio')),
    keepPreviousData: true,
    queryFn: async () => {
      const data = await mapsApi.mapPerfScreener(
        `?${new URLSearchParams({
          [ScreenerQuery.MapSubtype]: subtype,
          [ScreenerQuery.Tickers]: tickers,
          v: '711',
        })}`
      )

      return (
        data ??
        queryClient.getQueryData<PerfData>(['mapPerf', subtype]) ?? {
          ...FALLBACK_PERF_RESPONSE,
          version: dataVersion.current,
        }
      )
    },
    onSuccess(data) {
      if (!dataVersion.current && data) {
        dataVersion.current = data.version
      }

      if (dataVersion.current !== data?.version) {
        window.location.reload()
        return
      }
    },
  })

  return perfDataQuery
}
